import { Capacitor } from "@capacitor/core";
import { Purchases, LOG_LEVEL } from "@revenuecat/purchases-capacitor";

import { baseUrl, headersDefault } from "../../redux/actions";
import { normalizeProductPricing } from "./pricing";

// Single source of truth for the entitlement id seeded by the backend
// (config('revenuecat.default_entitlement') === 'master_club_premium').
export const ENTITLEMENT_ID = "master_club_premium";

let configured = false;

function isNative() {
  return Capacitor.getPlatform() !== "web";
}

function apiKeyForPlatform() {
  return Capacitor.getPlatform() === "ios"
    ? process.env.REACT_APP_REVENUECAT_PUBLIC_API_KEY_IOS
    : process.env.REACT_APP_REVENUECAT_PUBLIC_API_KEY_ANDROID;
}

/**
 * Configures the RC SDK with the logged-in user's app user id (= our user.id as
 * a string). Safe to call multiple times — the SDK's configure() is idempotent
 * for the same app user id, and we guard with `configured`. No-op on web.
 */
export async function initRevenueCat(userId) {
  if (!isNative()) return;
  if (userId == null) return;

  const apiKey = apiKeyForPlatform();
  if (!apiKey) {
    console.warn("[RevenueCat] missing public API key for platform", Capacitor.getPlatform());
    return;
  }

  try {
    if (process.env.NODE_ENV !== "production") {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    }
    await Purchases.configure({ apiKey, appUserID: String(userId) });
    configured = true;
  } catch (err) {
    console.error("[RevenueCat] configure failed", err);
  }
}

/**
 * Releases the cached RC user state on logout so a subsequent login can rebind
 * to the new app user id without leaking the previous user's offerings.
 */
export async function logOutRevenueCat() {
  if (!isNative() || !configured) return;
  try {
    await Purchases.logOut();
  } catch (err) {
    // RC throws if logging out an anonymous user — that's harmless here.
    console.warn("[RevenueCat] logOut warn", err?.message);
  }
}

/**
 * Returns the `default` offering's available packages, or null if the SDK isn't
 * configured / no offerings exist. The paywall renders these directly.
 */
export async function getOfferings() {
  if (!isNative() || !configured) return null;
  try {
    const result = await Purchases.getOfferings();
    return result?.current ?? null;
  } catch (err) {
    console.error("[RevenueCat] getOfferings failed", err);
    return null;
  }
}

/**
 * Returns the raw offerings payload `{ current, all }`. The shop widget IAP
 * bridge needs to scan packages across every offering (not just `current`),
 * because non-subscription products often live in separate offerings — and we
 * resolve them by store-SKU rather than by package identifier.
 */
export async function getAllOfferings() {
  if (!isNative() || !configured) return null;
  try {
    return await Purchases.getOfferings();
  } catch (err) {
    console.error("[RevenueCat] getAllOfferings failed", err);
    return null;
  }
}

/**
 * Builds a { [storeSku]: priceData } map from every package in every offering,
 * for the shop widget to display native store prices instead of the backend
 * product_prices rows. Key is the store product id with any Google base-plan
 * suffix stripped (":monthly"), matching how the backend stores
 * merchant_product_id (== the widget's product.pgProductId).
 *
 * priceData mirrors the widget's NativePrice shape (derived via
 * normalizeProductPricing so iOS StoreKit and Android Google Play multi-phase
 * pricing are handled identically):
 *   priceFull          - the recurring base price
 *   priceDiscounted    - intro/promo price if the package has one, else priceFull
 *   discountValue      - priceFull - priceDiscounted
 *   discountPercentage - discountValue / priceFull as a 0..1 fraction
 *   priceString        - the store-localized (discounted) price string
 *
 * Returns null on web / when offerings are unavailable so the widget keeps its
 * backend prices.
 */
/**
 * Returns a { [productId]: boolean } eligibility map for intro offers. `true`
 * means the user may still redeem the intro (or we couldn't determine it);
 * `false` ONLY when RevenueCat definitively reports the user is INELIGIBLE
 * (already used it / is or was subscribed). Fails safe to eligible so a
 * bad/absent response never hides a legitimate offer. This matters on iOS,
 * where StoreKit exposes `introPrice` on a product whenever one is configured,
 * independent of the current user's eligibility; on Android Google Play only
 * surfaces offers the user can actually get, so RC reports UNKNOWN and we leave
 * the offer visible.
 */
export async function getIntroEligibilityMap(productIds) {
  const map = {};
  if (!isNative() || !configured) return map;

  const ids = Array.from(new Set((productIds || []).filter(Boolean)));
  if (!ids.length) return map;

  // RevenueCat's INTRO_ELIGIBILITY_STATUS enum isn't exported at runtime by the
  // Capacitor plugin; the bridged `status` is the raw int (0 UNKNOWN,
  // 1 INELIGIBLE, 2 ELIGIBLE, 3 NO_INTRO_OFFER). Only 1 means "hide the offer".
  const INELIGIBLE = 1;

  try {
    const res = await Purchases.checkTrialOrIntroductoryPriceEligibility({
      productIdentifiers: ids,
    });
    // The plugin has returned the map either directly or under `.eligibility`
    // across versions — accept both.
    const raw = res?.eligibility ?? res ?? {};
    for (const id of ids) {
      const status = raw?.[id]?.status;
      map[id] = status !== INELIGIBLE; // eligible unless explicitly INELIGIBLE
    }
  } catch (err) {
    console.warn("[RevenueCat] intro eligibility check failed", err?.message);
    // Leave the map empty → callers treat a missing entry as eligible.
  }
  return map;
}

export async function getNativePriceMap() {
  const offerings = await getAllOfferings();
  if (!offerings) return null;

  const map = {};
  const all = offerings.all ?? {};

  // One eligibility round-trip for every product across all offerings, so an
  // ineligible (returning) user isn't shown an intro price they can't get.
  const productIds = [];
  for (const offeringId of Object.keys(all)) {
    for (const pkg of all[offeringId]?.availablePackages ?? []) {
      const id = pkg?.product?.identifier;
      if (id) productIds.push(id);
    }
  }
  const eligibility = await getIntroEligibilityMap(productIds);

  for (const offeringId of Object.keys(all)) {
    const packages = all[offeringId]?.availablePackages ?? [];
    for (const pkg of packages) {
      const product = pkg?.product;
      const rawId = product?.identifier;
      if (!rawId) continue;

      const sku = rawId.split(":")[0];

      // Normalize across stores: on iOS this reads product.price/introPrice, on
      // Android it walks the Google Play pricing phases (defaultOption /
      // subscriptionOptions) so the intro/promo offer is detected correctly and
      // the base recurring price is used as `priceFull` — not the other way
      // round. `introEligible:false` collapses the offer to the standard price.
      // See ./pricing for the full rationale.
      const pricing = normalizeProductPricing(product, {
        introEligible: eligibility[rawId] !== false,
      });
      const priceFull = Number(pricing?.priceFull);
      if (!Number.isFinite(priceFull) || priceFull <= 0) continue;

      const priceDiscounted = Number.isFinite(pricing?.priceDiscounted)
        ? Math.min(pricing.priceDiscounted, priceFull)
        : priceFull;
      const discountValue = Math.max(0, priceFull - priceDiscounted);

      // First writer wins so the `current` offering (iterated first by RC when
      // present) isn't clobbered by a duplicate SKU in another offering.
      if (!map[sku]) {
        map[sku] = {
          priceFull,
          priceDiscounted,
          discountValue,
          discountPercentage: priceFull > 0 ? discountValue / priceFull : 0,
          priceString: pricing?.priceDiscountedString ?? product?.priceString ?? undefined,
        };
      }
    }
  }

  return Object.keys(map).length ? map : null;
}

/**
 * Walks every package in every offering and returns the one whose underlying
 * store product identifier matches `sku`. Apple delivers `productIdentifier`
 * verbatim; Google delivers `parentSku:basePlanId` for subscriptions while our
 * backend stores `merchant_product_id = parentSku`, so we also accept a match
 * on the part before the colon.
 *
 * Returns `{ pkg, offeringIdentifier }` for diagnostics, or null when no
 * package matches.
 */
export function findPackageBySku(offerings, sku) {
  if (!offerings || !sku) return null;
  const all = offerings.all ?? {};
  for (const offeringId of Object.keys(all)) {
    const offering = all[offeringId];
    const packages = offering?.availablePackages ?? [];
    for (const pkg of packages) {
      const productId = pkg?.product?.identifier ?? "";
      if (!productId) continue;
      if (productId === sku || productId.split(":")[0] === sku) {
        return { pkg, offeringIdentifier: offering?.identifier ?? offeringId };
      }
    }
  }
  return null;
}

/**
 * Premium Lite ↔ Premium on Google Play (Premium Lite Phase 4): the purchase
 * must name the subscription it replaces, or Google sells a second one next
 * to it. Upgrade = charge the full price now (the unused time becomes extra
 * days; CHARGE_PRORATED_PRICE fails when the new plan is cheaper per day);
 * downgrade = from the renewal. Apple handles both inside the subscription
 * group, and the iOS plugin ignores this option. Kept here (strings) so the
 * pages and their tests never load the native plugin.
 */
export const PLAN_CHANGE_MODE = {
  UPGRADE: "CHARGE_FULL_PRICE",
  DOWNGRADE: "DEFERRED",
};

/**
 * Triggers the StoreKit / Play Billing purchase sheet for the chosen package.
 * Resolves with the entitlement state RC reports post-purchase. Throws on
 * user cancellation only when `throwOnCancel` is true; otherwise returns
 * { cancelled: true } so callers can show a friendly message.
 *
 * `planChange` ({ oldProductIdentifier, replacementMode }) turns the purchase
 * into a plan change on Google Play.
 */
export async function purchasePackage(rcPackage, planChange = null) {
  if (!isNative() || !configured) {
    throw new Error("RevenueCat is not available on this platform.");
  }
  try {
    const options = { aPackage: rcPackage };
    if (planChange?.oldProductIdentifier && planChange?.replacementMode) {
      options.storeProductChangeInfo = {
        // Google expects the subscription id, without ":basePlan".
        oldProductIdentifier: String(planChange.oldProductIdentifier).split(":")[0],
        replacementMode: planChange.replacementMode,
      };
    }
    const result = await Purchases.purchasePackage(options);
    return {
      cancelled: false,
      customerInfo: result?.customerInfo ?? null,
      // The SKU actually bought: reconcile must send this one, not whatever
      // product the entitlement reports (Premium and Premium Lite share the
      // `master_club_premium` entitlement, so the tier comes from the SKU).
      // The transaction is deliberately not passed on: its id doesn't always
      // equal RevenueCat's subscription id (Android order id, Apple plan
      // changes), and the backend discovers the right one when it is absent.
      productIdentifier:
        result?.productIdentifier ??
        result?.transaction?.productIdentifier ??
        rcPackage?.product?.identifier ??
        null,
    };
  } catch (err) {
    if (err?.userCancelled) {
      return { cancelled: true, customerInfo: null };
    }
    throw err;
  }
}

/**
 * Maps RC's per-store identifiers to the value the backend's RevenueCat webhook
 * expects in the `store` field. The Capacitor SDK reports store as a string we
 * normalize to APP_STORE / PLAY_STORE; anything else (Stripe, Amazon, Mac App
 * Store) is rejected by the reconcile endpoint and we don't try to recover.
 */
function normalizeStore(rcStore) {
  if (rcStore === "APP_STORE" || rcStore === "MAC_APP_STORE") return "APP_STORE";
  if (rcStore === "PLAY_STORE") return "PLAY_STORE";
  return null;
}

/**
 * Builds the request body for POST /api/v1/billing/reconcile from a successful
 * purchasePackage() result. The backend only requires `product_id` — every
 * other field is optional and discovered server-side via RC's REST API when
 * missing. This matters because RC's Capacitor SDK does NOT reliably expose
 * `transaction.transactionIdentifier` on Android sandbox (we verified live:
 * the SDK's own log shows transaction as Object, but the value passed to JS
 * comes through as null), so we can't gate the request on its presence.
 *
 * Strategy:
 *  - Always send `product_id` (always present as result.productIdentifier)
 *  - Send `transaction_id` IF we can extract it (best-effort, backend will
 *    fall back to discovering via RC if absent)
 *  - Send `store` derived from platform when the entitlement object's store
 *    field is empty (also a sandbox quirk)
 *
 * Returns null only if we don't even have a productIdentifier — at that
 * point the result is too degenerate to reconcile against anyway.
 */
function extractReceiptForReconcile(purchaseResult) {
  const customerInfo = purchaseResult?.customerInfo ?? null;
  const entitlement = customerInfo?.entitlements?.active?.[ENTITLEMENT_ID] ?? null;
  const tx = purchaseResult?.transaction ?? null;

  const productId =
    purchaseResult?.productIdentifier ??
    tx?.productIdentifier ??
    entitlement?.productIdentifier ??
    customerInfo?.allPurchasedProductIdentifiers?.[0] ??
    null;
  if (!productId) {
    console.warn(
      "[RevenueCat] reconcile skipped: no product id in purchase result",
      { keys: Object.keys(purchaseResult ?? {}) },
    );
    return null;
  }

  // Best-effort transaction id extraction. The Capacitor bridge's exact field
  // name has varied across versions — accept any documented form. The backend
  // will discover the canonical id via RC if we send null.
  const transactionId =
    tx?.transactionIdentifier ??
    tx?.transactionId ??
    tx?.id ??
    purchaseResult?.transactionIdentifier ??
    null;

  // Same fallback story for store. Prefer the entitlement value, fall back
  // to the platform — both branches resolve to the webhook-compatible
  // APP_STORE / PLAY_STORE alphabet that PurchaseActivator expects.
  const store =
    normalizeStore(entitlement?.store) ??
    (Capacitor.getPlatform() === "ios" ? "APP_STORE" : null) ??
    (Capacitor.getPlatform() === "android" ? "PLAY_STORE" : null);

  const purchaseDateMs =
    tx?.purchaseDateMillis ??
    (tx?.purchaseDate ? Date.parse(tx.purchaseDate) : null) ??
    (entitlement?.latestPurchaseDateMillis ?? null);

  return {
    transaction_id: transactionId ? String(transactionId) : null,
    original_transaction_id: null,
    product_id: String(productId),
    store,
    purchased_at_ms: Number.isFinite(purchaseDateMs) ? purchaseDateMs : null,
  };
}

/**
 * Asks the backend to verify a freshly-completed IAP against RevenueCat's REST
 * API and create the corresponding Order + MasterClubSubscription immediately,
 * without waiting for the asynchronous INITIAL_PURCHASE webhook to land.
 *
 * Idempotent on the server side via Order.merchant_transaction_id, so it's safe
 * for both the reconcile endpoint and the webhook to run for the same purchase.
 *
 * Returns one of "reconciled" | "not_found" | "no_access" | "unreachable" | "error".
 * Callers should treat anything other than "reconciled" as "fall back to the
 * existing polling/optimistic path" rather than as a hard failure — the worst
 * case is the user briefly sees the pre-reconcile UX while the webhook catches up.
 */
export async function reconcilePurchaseWithBackend(purchaseResult, { timeoutMs = 10000 } = {}) {
  const payload = extractReceiptForReconcile(purchaseResult);
  if (!payload) return "error";

  console.log("[RevenueCat] reconcile -> POST /api/v1/billing/reconcile", payload);

  const controller = new AbortController();
  const timeoutHandle = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${baseUrl}/api/v1/billing/reconcile`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: headersDefault(),
      signal: controller.signal,
    });
    const data = await response.json().catch(() => null);
    console.log("[RevenueCat] reconcile <- response", { status: response.status, body: data });
    return data?.status ?? "error";
  } catch (err) {
    if (err?.name === "AbortError") {
      console.warn("[RevenueCat] reconcile aborted (timeout)");
      return "unreachable";
    }
    console.warn("[RevenueCat] reconcile call failed", err);
    return "unreachable";
  } finally {
    clearTimeout(timeoutHandle);
  }
}

/**
 * Mandatory under Apple's App Store Review Guideline 3.1.1 — surfaces a button
 * the user can tap to restore prior purchases (e.g. after reinstall). Returns
 * the refreshed customerInfo so callers can refetch entitlements.
 */
export async function restorePurchases() {
  if (!isNative() || !configured) {
    throw new Error("RevenueCat is not available on this platform.");
  }
  const result = await Purchases.restorePurchases();
  return result?.customerInfo ?? null;
}

/**
 * Called after Apple "Hide My Email" account recovery: the authenticated user id
 * has just changed from the throwaway relay stub to the recovered account, so we
 * re-bind RevenueCat to the recovered id and pull the device's store entitlements
 * onto it. This makes an IAP subscription bought under the recovered account
 * reappear immediately (a Stripe/web sub needs no store sync). Native-only and
 * best-effort — returns the customerInfo (or null) and never throws.
 */
export async function syncEntitlementsForRecoveredUser(userId) {
  if (!isNative() || userId == null) return null;
  try {
    await initRevenueCat(userId);
    const customerInfo = await restorePurchases();
    if (hasActiveMasterClubPremium(customerInfo)) {
      // Create the canonical Order/subscription immediately instead of waiting
      // for the webhook; idempotent against it. Non-fatal if it can't reach.
      await reconcilePurchaseWithBackend({ customerInfo }).catch(() => {});
    }
    return customerInfo;
  } catch (err) {
    console.warn("[RevenueCat] recovery entitlement sync failed", err?.message);
    return null;
  }
}

/**
 * Convenience: returns true iff the SDK reports `master_club_premium` as a
 * currently-active entitlement. Used by the paywall pre-render guard.
 */
/** The store product behind the active paid entitlement (Lite and Premium share it). */
export async function getActiveProductIdentifier() {
  if (!isNative() || !configured) return null;
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return customerInfo?.entitlements?.active?.[ENTITLEMENT_ID]?.productIdentifier ?? null;
  } catch (err) {
    console.error("[RevenueCat] getCustomerInfo failed", err);
    return null;
  }
}

export function hasActiveMasterClubPremium(customerInfo) {
  return Boolean(customerInfo?.entitlements?.active?.[ENTITLEMENT_ID]);
}

let lastObservedActive = null;
// undefined = nothing observed yet (null = no active product).
let lastObservedProduct = undefined;
let listenerHandle = null;

/**
 * Subscribes to RC's customer-info update stream. RC's SDK fires this callback
 * any time it observes an entitlement change — including when Apple/Google push
 * down a CANCELLATION or EXPIRATION via their server-to-device sync. Without
 * this, the only way the app learns about cancellations is on next cold start
 * (and even then the throttled getUserInfo may serve stale data).
 *
 * The handler debounces: we only call `onEntitlementChanged()` when the
 * "is master_club_premium currently active?" boolean actually flips from
 * what it was last time RC reported. RC can fire updates for unrelated reasons
 * (transaction sync, customer attribute change, etc.) and we don't want to
 * thrash the user-info endpoint on every one.
 */
export async function subscribeToEntitlementUpdates(onEntitlementChanged) {
  if (!isNative() || !configured) return () => {};
  if (listenerHandle) return listenerHandle;

  try {
    listenerHandle = await Purchases.addCustomerInfoUpdateListener(
      (customerInfo) => {
        const isActive = hasActiveMasterClubPremium(customerInfo);
        // Premium Lite ↔ Premium keeps the entitlement active: the product
        // changing is a change of plan too.
        const product =
          customerInfo?.entitlements?.active?.[ENTITLEMENT_ID]?.productIdentifier ?? null;
        const productChanged =
          lastObservedProduct !== undefined && product !== lastObservedProduct;
        if (lastObservedActive !== null && lastObservedActive === isActive && !productChanged) {
          return; // no flip — ignore noise
        }
        lastObservedActive = isActive;
        lastObservedProduct = product;
        try {
          onEntitlementChanged?.({ active: isActive, customerInfo });
        } catch (err) {
          console.error("[RevenueCat] entitlement-change handler threw", err);
        }
      },
    );
  } catch (err) {
    console.error("[RevenueCat] addCustomerInfoUpdateListener failed", err);
  }

  return () => {
    if (listenerHandle?.remove) {
      try {
        listenerHandle.remove();
      } catch (_) {
        // RC versions before 8.x have no `.remove()`; harmless to ignore.
      }
    }
    listenerHandle = null;
    lastObservedActive = null;
    lastObservedProduct = undefined;
  };
}
